import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import SettingLabel from "./SettingLabel";

const SocialUsernameSettingsOption = (props: Props) => {
  const {
    user,
    company,
    name,
    label,
    placeholder,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
    noCancel,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values[name] === company[name]) {
      onEditComplete?.();
      return;
    }

    try {
      company.assignAttributes(values);
      const success = await company.save();
      if (success) {
        onEditComplete?.();
      }
      else {
        const error_message =
        name === "twitterUsername"
        ? company.errors[name]?.message
        : company.errors[name]?.fullMessage;

        formik.setErrors({
          [name]: error_message,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      formik.setErrors({
        [name]: "Uh oh, something went wrong there",
      });
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart();
    }
  };

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>{label}</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {company[name] ? <div>@{company[name]}</div> : <div>Not specified</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    [name]: company[name] ?? "",
  };

  const validation = Yup.object({
    [name]: Yup.string(),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>{label}</SettingLabel>
            {!noCancel && <TextLink onClick={onEditComplete}>Cancel</TextLink>}
          </Header>
          <HintText>
            You only need to enter your username, don&apos;t include the @!
          </HintText>
          <TextInputField
            label={label}
            prefixDisplay="@"
            hiddenLabel
            name={name}
            placeholder={placeholder}
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type FormValues = {
  twitterUsername?: string;
  instagramUsername?: string;
};

type Props = {
  user: RecruiterUser | EmployerUser;
  company: Recruiter | Employer;
  label: string;
  name: keyof FormValues;
  placeholder: string;
  isEditing?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onEditStart?: () => void;
  onEditComplete?: () => void;
  noCancel?: boolean;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const HintText = styled.div`
  color: ${({ theme }) => theme.colors.gray500};
  margin-bottom: 1rem;
`;

export default SocialUsernameSettingsOption;
